import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Check, ChevronLeft, ChevronRight, KeyRound, ShieldQuestion } from "lucide-react";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ceo-operacional/artifacts/2hvzdama_LogocomSlogan-fundoescuro.png";

const STEPS = [
  { icon: KeyRound, title: "Senha administrativa", text: "Defina a credencial principal com os requisitos mínimos de segurança." },
  { icon: ShieldQuestion, title: "Recuperação de acesso", text: "Escolha uma pergunta de segurança para recuperar a conta quando necessário." },
];

export default function Setup() {
  const navigate = useNavigate();
  const { setup, questionsList, reload } = useAuth();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    password: "", password_confirm: "",
    security_question: "", security_answer: "",
  });
  const [loading, setLoading] = useState(false);

  const validateAccess = () => {
    if (form.password.length < 12) return "Senha precisa ter no mínimo 12 caracteres";
    if (!/[A-Z]/.test(form.password)) return "Inclua pelo menos uma letra maiúscula";
    if (!/[a-z]/.test(form.password)) return "Inclua pelo menos uma letra minúscula";
    if (!/[0-9]/.test(form.password)) return "Inclua pelo menos um número";
    if (!/[^a-zA-Z0-9]/.test(form.password)) return "Inclua pelo menos um símbolo";
    if (form.password !== form.password_confirm) return "As senhas não conferem";
    return null;
  };

  const validateSecurity = () => {
    if (!form.security_question) return "Selecione uma pergunta";
    if (form.security_answer.length < 1) return "Informe a resposta";
    return null;
  };

  const next = () => {
    const err = step === 0 ? validateAccess() : validateSecurity();
    if (err) return toast.error(err);
    if (step === 0) setStep(1);
  };

  const submit = async () => {
    const err = validateSecurity();
    if (err) return toast.error(err);
    setLoading(true);
    try {
      await setup({
        password: form.password,
        security_question: form.security_question,
        security_answer: form.security_answer,
      });
      toast.success("Configuração concluída!");
      await reload();
      navigate("/login");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao configurar");
    } finally { setLoading(false); }
  };

  const ActiveIcon = STEPS[step].icon;

  return (
    <div className="relative min-h-screen overflow-hidden bg-background px-4 py-8 sm:px-6">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-96 bg-gradient-to-b from-primary/10 to-transparent" />
      <div className="pointer-events-none absolute -right-28 top-24 size-80 rounded-full bg-primary/10 blur-3xl" />

      <div className="relative mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-5xl items-center">
        <div className="grid w-full overflow-hidden rounded-3xl border border-border/80 bg-card/90 shadow-[0_1px_2px_hsl(var(--foreground)/0.04),0_30px_90px_hsl(var(--foreground)/0.08)] backdrop-blur-sm lg:grid-cols-[0.88fr_1.12fr]" data-testid="setup-form">
          <aside className="relative hidden overflow-hidden bg-[hsl(154_40%_4%)] p-8 text-white lg:flex lg:flex-col">
            <div className="pointer-events-none absolute -left-20 -top-20 size-72 rounded-full bg-primary/15 blur-3xl" />
            <img src={LOGO_URL} alt="DEMoraes" className="relative h-16 w-auto object-contain object-left" />

            <div className="relative my-auto py-10">
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/35">Configuração segura</div>
              <h1 className="mt-3 text-3xl font-semibold leading-tight tracking-[-0.045em]">Prepare o ambiente administrativo.</h1>
              <p className="mt-3 text-sm leading-relaxed text-white/50">Duas etapas rápidas para proteger o acesso principal e habilitar recuperação controlada.</p>

              <div className="mt-8 space-y-3">
                {STEPS.map(({ icon: Icon, title, text }, index) => {
                  const active = index === step;
                  const complete = index < step;
                  return (
                    <div key={title} className={`flex gap-3 rounded-2xl border p-4 transition ${active ? "border-primary/25 bg-primary/10" : "border-white/[0.06] bg-white/[0.025]"}`}>
                      <div className={`grid size-9 shrink-0 place-items-center rounded-xl ${active || complete ? "bg-primary/15 text-emerald-300" : "bg-white/5 text-white/35"}`}>
                        {complete ? <Check className="size-4" /> : <Icon className="size-4" />}
                      </div>
                      <div>
                        <div className={`text-sm font-medium ${active ? "text-white" : "text-white/65"}`}>{index + 1}. {title}</div>
                        <div className="mt-0.5 text-xs leading-relaxed text-white/40">{text}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </aside>

          <section className="p-5 sm:p-8 lg:p-10">
            <div className="mb-7 lg:hidden">
              <img src={LOGO_URL} alt="DEMoraes" className="mb-5 h-14 w-auto object-contain" />
            </div>

            <div className="mb-7 flex items-start gap-4">
              <div className="grid size-11 shrink-0 place-items-center rounded-xl border border-primary/15 bg-primary/10 text-primary">
                <ActiveIcon className="size-5" />
              </div>
              <div>
                <div className="label-tech">Etapa {step + 1} de 2</div>
                <h2 className="mt-1 text-2xl font-semibold tracking-[-0.04em]">{STEPS[step].title}</h2>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{STEPS[step].text}</p>
              </div>
            </div>

            <div className="mb-7 grid grid-cols-2 gap-2 lg:hidden">
              {STEPS.map((item, index) => (
                <div key={item.title} className={`h-1.5 rounded-full ${index <= step ? "bg-primary" : "bg-muted"}`} />
              ))}
            </div>

            {step === 0 && (
              <div className="space-y-5">
                <div className="rounded-xl border border-border/70 bg-muted/25 p-4 text-xs leading-relaxed text-muted-foreground">
                  Use pelo menos 12 caracteres, incluindo maiúscula, minúscula, número e símbolo. Evite reutilizar uma senha de outros serviços.
                </div>
                <div className="space-y-1.5">
                  <Label className="label-tech">Senha</Label>
                  <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="setup-password-input" placeholder="Crie uma senha forte" />
                </div>
                <div className="space-y-1.5">
                  <Label className="label-tech">Confirmar senha</Label>
                  <Input type="password" value={form.password_confirm} onChange={(e) => setForm({ ...form, password_confirm: e.target.value })} data-testid="setup-password-confirm-input" placeholder="Repita a senha" />
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-5">
                <div className="rounded-xl border border-border/70 bg-muted/25 p-4 text-xs leading-relaxed text-muted-foreground">
                  A pergunta de segurança será usada somente no fluxo de recuperação administrativa. Escolha uma resposta memorável, mas difícil de adivinhar.
                </div>
                <div className="space-y-1.5">
                  <Label className="label-tech">Pergunta</Label>
                  <Select value={form.security_question} onValueChange={(v) => setForm({ ...form, security_question: v })}>
                    <SelectTrigger data-testid="setup-security-question-select"><SelectValue placeholder="Selecione uma pergunta" /></SelectTrigger>
                    <SelectContent>
                      {questionsList.map((q) => (<SelectItem key={q} value={q} data-testid={`setup-question-option-${q}`}>{q}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="label-tech">Resposta</Label>
                  <Input value={form.security_answer} onChange={(e) => setForm({ ...form, security_answer: e.target.value })} data-testid="setup-security-answer-input" placeholder="Sua resposta" />
                  <p className="text-xs text-muted-foreground">Acentos, maiúsculas e espaços extras serão ignorados.</p>
                </div>
              </div>
            )}

            <div className="mt-8 flex items-center justify-between gap-3 border-t border-border/70 pt-6">
              <Button variant="outline" onClick={() => setStep(step - 1)} disabled={step === 0} data-testid="setup-back-btn">
                <ChevronLeft className="size-4" /> Voltar
              </Button>
              {step < 1 ? (
                <Button onClick={next} data-testid="setup-next-btn">
                  Próximo <ChevronRight className="size-4" />
                </Button>
              ) : (
                <Button onClick={submit} disabled={loading} data-testid="setup-submit-btn">
                  {loading ? "Configurando..." : "Concluir configuração"}
                </Button>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
